// Suppliers for the event and their qualification per part (Lane 1322).
// Only the buyer's own supplier records can be added. The approved list sets
// each part's default; a buyer change needs a short reason (evidence).
import React, { useCallback, useEffect, useState } from 'react';
import { TableShell, Pager } from '../../components/ui/DataPrimitives';
import { SupplierPicker } from './ScopeBuilder';
import { STALE_MESSAGE } from './sourcingApi';

const STATE_TEXT = { approved: 'Approved', not_approved: 'Not approved', in_progress: 'In progress' };

// Lane 1438 (Ryan, XMEK's first RFP): a check mark for approved, nothing for not
// approved, the words for in progress; a buyer's change keeps its * marker. The state
// in words stays on the button for screen readers.
function CheckMark() {
  return (
    <svg className="srce-qcell-mark" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path d="M3 8.5l3.2 3.2L13 4.8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function QualificationCell({ cell, editable, onSet, onClear }) {
  const [editing, setEditing] = useState(false);
  const [state, setState] = useState(cell.state);
  const [evidence, setEvidence] = useState('');
  if (!editing) {
    const byBuyer = cell.source === 'buyer';
    return (
      <button type="button" className={`srce-qcell srce-qcell--${cell.state}`} disabled={!editable}
        aria-label={`${STATE_TEXT[cell.state]}${byBuyer ? ', changed by a buyer' : ''}`}
        title={byBuyer ? `${STATE_TEXT[cell.state]}. Set by ${cell.set_by_name || 'a buyer'}: ${cell.evidence}` : `${STATE_TEXT[cell.state]}, from the approved list`}
        onClick={() => { setState(cell.state); setEvidence(''); setEditing(true); }}>
        {cell.state === 'approved' && <CheckMark />}
        {cell.state === 'in_progress' && 'In progress'}
        {byBuyer ? ' *' : ''}
      </button>
    );
  }
  return (
    <div className="srce-q-edit">
      <select className="ll-sel" value={state} onChange={(e) => setState(e.target.value)} aria-label="Qualification">
        <option value="approved">Approved</option>
        <option value="in_progress">In progress</option>
        <option value="not_approved">Not approved</option>
      </select>
      <input className="srce-input" value={evidence} maxLength={4000} placeholder="Why? (required)" aria-label="Reason"
        onChange={(e) => setEvidence(e.target.value)} />
      <button type="button" className="srce-btn srce-btn--primary" disabled={!evidence.trim()}
        onClick={() => { setEditing(false); onSet(state, evidence.trim()); }}>Save</button>
      {cell.source === 'buyer' && (
        <button type="button" className="srce-btn srce-btn--quiet" onClick={() => { setEditing(false); onClear(); }}>Use approved list</button>
      )}
      <button type="button" className="ll-clr" onClick={() => setEditing(false)}>Cancel</button>
    </div>
  );
}

export default function SuppliersTab({ api, eventId, summary, onChanged }) {
  const editable = ['draft', 'open', 'closed'].includes(summary.status);
  const canChangeSuppliers = summary.status === 'draft';
  const [suppliers, setSuppliers] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [page, setPage] = useState(1);
  // Load errors are kept apart from action errors, so the reload that follows
  // a refused (stale) change cannot wipe the message about it.
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');

  const load = useCallback(() => {
    Promise.all([api.suppliers(eventId), api.qualifications(eventId, { page, page_size: 50 })])
      .then(([s, q]) => { setSuppliers(s); setMatrix(q); setLoadError(''); })
      .catch((e) => setLoadError(e.message));
  }, [api, eventId, page]);
  // summary.revision: a change made elsewhere (chat) reloads the lists too.
  useEffect(() => { load(); }, [load, summary.revision]);

  const act = async (method, path, input) => {
    setError('');
    try {
      await api.act(eventId, method, path, input, summary.revision);
      await onChanged();
      load();
    } catch (e) {
      if (e.code === 'stale_revision') { await onChanged(); load(); }
      setError(e.code === 'stale_revision' ? STALE_MESSAGE : e.message);
    }
  };
  const remove = async (supplierId) => {
    setError('');
    try {
      await api.actQuery(eventId, 'DELETE', `/suppliers/${supplierId}`, summary.revision);
      await onChanged();
      load();
    } catch (e) {
      if (e.code === 'stale_revision') { await onChanged(); load(); }
      setError(e.code === 'stale_revision' ? STALE_MESSAGE : e.message);
    }
  };

  return (
    <div>
      {canChangeSuppliers && (
        <div className="ll-fbar">
          <span className="srce-label">Add a supplier</span>
          <SupplierPicker api={api} value={null}
            onChange={(s) => { if (s) act('POST', '/suppliers', { supplier_id: s.supplier_id }); }} />
          <span className="srce-mut">Suppliers are not contacted until the event is published.</span>
        </div>
      )}
      {error && <div className="srce-error" role="alert">{error}</div>}
      {loadError && <div className="srce-error" role="alert">{loadError}</div>}
      {suppliers && suppliers.suppliers.length === 0 && (
        <div className="ll-empty"><div className="ll-empty-t">No suppliers yet</div>Add the suppliers you want to ask for offers.</div>
      )}
      {suppliers && suppliers.suppliers.length > 0 && (
        <TableShell className="srce-tbl srce-tbl--sups" aria-label="Suppliers in this event">
          <div className="srce-tr srce-th">
            <div>Supplier</div><div className="srce-r">Approved</div><div className="srce-r">In progress</div>
            <div className="srce-r">Not approved</div><div />
          </div>
          {suppliers.suppliers.map((s) => (
            <div key={s.supplier_id} className="srce-tr">
              <div className="srce-strong">{s.supplier_name}{s.is_incumbent && <span className="srce-tag">Current supplier</span>}</div>
              <div className="srce-r">{s.qualification.approved} of {suppliers.parts}</div>
              <div className="srce-r">{s.qualification.in_progress}</div>
              <div className="srce-r">{s.qualification.not_approved}</div>
              <div className="srce-r">{canChangeSuppliers && <button type="button" className="ll-clr" onClick={() => remove(s.supplier_id)}>Remove</button>}</div>
            </div>
          ))}
        </TableShell>
      )}
      {matrix && matrix.suppliers.length > 0 && matrix.total > 0 && (
        <>
          <h2 className="srce-h2">Qualification by part</h2>
          <div className="srce-mut srce-gap">A check mark means approved; an empty cell means not approved. From the approved list unless a buyer changed it (marked *). Click a cell to change it.</div>
          <div className="srce-scroll">
            <table className="srce-grid">
              <thead><tr><th>Part</th>{matrix.suppliers.map((s) => <th key={s.supplier_id}>{s.supplier_name}</th>)}</tr></thead>
              <tbody>
                {matrix.items.map((row) => (
                  <tr key={row.material_number}>
                    <td className="srce-mono">{row.material_number}</td>
                    {row.cells.map((c) => (
                      <td key={c.supplier_id}>
                        <QualificationCell cell={c} editable={editable}
                          onSet={(state, evidence) => act('PUT', '/qualifications', { material_number: row.material_number, supplier_id: c.supplier_id, state, evidence })}
                          onClear={() => act('POST', '/qualifications/clear', { material_number: row.material_number, supplier_id: c.supplier_id })} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager data={matrix} onPage={setPage} label="parts" numbered />
        </>
      )}
    </div>
  );
}
